const LOGIN_EVENT_PENDING_KEY = "loginEventPending";
const LOGIN_EVENT_IDENTITY_KEY = "loginEventIdentity";
const LOGIN_EVENT_EMITTED_IDENTITY_KEY = "loginEventEmittedIdentity";
const LOGIN_OCCURRENCE_ID_KEY = "loginOccurrenceId";

// Window-scoped "already emitted this page load" latch. Within a single page
// load it stops an auth re-entry loop (which re-arms the pending marker; see
// `applyB2CIdentityToAccount` in `b2c-utils.ts`) from producing more than one
// POST. It does NOT survive navigation, so cross-redirect de-duplication relies
// on the persisted "emitted identity" below.
type PageLoadGuard = { __ltlLoginEventEmitted?: boolean };
const pageLoadGuard = globalThis as typeof globalThis & PageLoadGuard;

let fallbackIdentityCounter = 0;

const generateLoginFlowIdentity = (): string => {
	try {
		const uuid = globalThis.crypto?.randomUUID?.();
		if (uuid) {
			return uuid;
		}
	} catch {
		// crypto may be unavailable; fall through to a counter-based identity.
	}
	fallbackIdentityCounter += 1;
	return `${Date.now()}-${fallbackIdentityCounter}`;
};

const clearPendingMarker = (): void => {
	try {
		globalThis.sessionStorage?.removeItem(LOGIN_EVENT_PENDING_KEY);
		globalThis.sessionStorage?.removeItem(LOGIN_EVENT_IDENTITY_KEY);
	} catch {
		// sessionStorage may be unavailable (privacy mode); nothing to clear.
	}
};

/**
 * Returns a stable identifier for the current authentication occurrence,
 * persisted for the browser session. It is created lazily on first use and
 * reused across the token-acquisition redirects of one login — so a re-issued ID
 * token (e.g. a popup→`acquireTokenRedirect` fallback, whose `iat` changes) does
 * not look like a new login and duplicate the event — and reset only at a
 * deliberate logout boundary via {@link clearLoginOccurrenceId}.
 */
export const getOrCreateLoginOccurrenceId = (): string => {
	try {
		const existing = globalThis.sessionStorage?.getItem(
			LOGIN_OCCURRENCE_ID_KEY,
		);
		if (existing) {
			return existing;
		}
		const occurrenceId = generateLoginFlowIdentity();
		globalThis.sessionStorage?.setItem(LOGIN_OCCURRENCE_ID_KEY, occurrenceId);
		return occurrenceId;
	} catch {
		// sessionStorage unavailable (privacy mode); a transient id is acceptable.
		return generateLoginFlowIdentity();
	}
};

/**
 * Clears the persisted authentication-occurrence identifier so the next login
 * starts a fresh occurrence. Call at a deliberate logout boundary.
 */
export const clearLoginOccurrenceId = (): void => {
	try {
		globalThis.sessionStorage?.removeItem(LOGIN_OCCURRENCE_ID_KEY);
	} catch {
		// sessionStorage unavailable; nothing to clear.
	}
};

/**
 * Records that a login-events emission is owed so the side effect can fire
 * exactly once per login flow. Persisted in sessionStorage (not component
 * state) so the marker survives both the render churn between MSAL
 * initialization and customer resolution and the full-page navigation of a
 * login or profile switch. It is set only on deliberate re-entry into a
 * customer context — a genuine B2C login (see `applyB2CIdentityToAccount` in
 * `b2c-utils.ts`) or a profile switch (see `profile-switcher.tsx`). A passive
 * refresh writes no marker, so it records no spurious login.
 *
 * `identity` is a stable fingerprint of the login flow: the same across the
 * redirects of one authentication, but different for a genuinely new login or
 * profile switch. It lets {@link consumeLoginEventPending} de-duplicate across
 * the multiple auth redirects of a single flow (which each reset the
 * window-scoped latch) without suppressing a real new login. When omitted a
 * unique identity is generated, so the emission is always treated as new.
 */
export const markLoginEventPending = (identity?: string): void => {
	try {
		globalThis.sessionStorage?.setItem(LOGIN_EVENT_PENDING_KEY, "true");
		globalThis.sessionStorage?.setItem(
			LOGIN_EVENT_IDENTITY_KEY,
			identity ?? generateLoginFlowIdentity(),
		);
	} catch {
		// sessionStorage may be unavailable (privacy mode); the login event
		// simply won't be recorded, which is acceptable for this telemetry.
	}
};

/**
 * Returns whether a login-events emission is pending, consuming it at most once
 * per login flow.
 *
 * Two guards cooperate:
 * - A window-scoped latch stops an auth re-entry loop from emitting twice within
 *   a single page load.
 * - A persisted "emitted identity" stops a *later* auth redirect in the same
 *   login flow (a fresh page load, so the window latch is reset) from emitting
 *   again, because the login handler re-arms the marker with the same identity.
 *   A genuinely new login or profile switch carries a different identity and is
 *   therefore still emitted.
 *
 * The emitted identity is persisted at consume time (best effort, at most once
 * per flow). We deliberately do NOT wait for POST success: there is no
 * end-to-end idempotency key, so retrying a lost/failed delivery would risk
 * duplicate rows. Preventing duplicates is the priority; a rare missed login
 * event is acceptable.
 */
export const consumeLoginEventPending = (): boolean => {
	if (pageLoadGuard.__ltlLoginEventEmitted) {
		// Already emitted this page load; clear any re-armed marker so it cannot
		// leak to a later reload.
		clearPendingMarker();
		return false;
	}
	try {
		const pending =
			globalThis.sessionStorage?.getItem(LOGIN_EVENT_PENDING_KEY) === "true";
		if (!pending) {
			return false;
		}

		const identity =
			globalThis.sessionStorage?.getItem(LOGIN_EVENT_IDENTITY_KEY) ?? "";
		const emittedIdentity = globalThis.sessionStorage?.getItem(
			LOGIN_EVENT_EMITTED_IDENTITY_KEY,
		);
		clearPendingMarker();

		// A prior redirect in the SAME login flow already emitted; suppress the
		// duplicate. A new login or profile switch carries a different identity.
		if (emittedIdentity != null && emittedIdentity === identity) {
			return false;
		}

		globalThis.sessionStorage?.setItem(
			LOGIN_EVENT_EMITTED_IDENTITY_KEY,
			identity,
		);
		pageLoadGuard.__ltlLoginEventEmitted = true;
		return true;
	} catch {
		return false;
	}
};
